"use client"

import {
  useDeleteCVDocument,
  PersonListCard,
  Badge,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cvStatusBadgeVariant,
  formatCvStatusLabel,
  talentLocationLine,
  talentMetadataLine,
  talentTopSkills,
  cvDocumentToPersonModel,
  openToWorkTitleBadge,
  formatBriefDate,
  cn,
} from '@yeshuman/sdk'
import type { CVDocument } from '@yeshuman/sdk'
import * as React from 'react'
import { SaveToBriefControl } from './save-to-brief-control'
import { MapPin, Trash2, Calendar } from 'lucide-react'
import { toast } from 'sonner'

export type TalentProfileCardProps = {
  document: CVDocument
  variant?: 'default' | 'compact' | 'hub'
  onOpen?: () => void
  onDeleted?: () => void
  className?: string
}

export function TalentProfileCard({
  document,
  variant = 'default',
  onOpen,
  onDeleted,
  className,
}: TalentProfileCardProps) {
  const { deleteCV, isDeleting } = useDeleteCVDocument()
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const preview = document.profile_preview
  const personModel = cvDocumentToPersonModel(document)
  const displayName = personModel.name
  const roleLine = personModel.subtitle
  const locationLine = talentLocationLine(preview)
  const metadataLine = talentMetadataLine(preview)
  const skillLimit = variant === 'default' ? 5 : 3
  const skills = talentTopSkills(preview, skillLimit)
  const canPreview = document.parsing_status === 'completed' && !!document.parsed_profile_id
  const isCompact = variant === 'compact' || variant === 'hub'
  const isHub = variant === 'hub'
  const showSaveToBrief = canPreview && !isHub
  const showRemove = !isCompact
  const cardVariant = isCompact ? 'compact' : 'standard'

  const handleDelete = React.useCallback(async () => {
    try {
      await deleteCV(document.id)
      toast.success('Removed from talent')
      setDeleteOpen(false)
      onDeleted?.()
    } catch (error) {
      toast.error(
        `Failed to remove: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }, [deleteCV, document.id, onDeleted])

  const headerAside = (
    <div className="flex shrink-0 items-center gap-1">
      <Badge variant={cvStatusBadgeVariant(document.parsing_status)}>
        {formatCvStatusLabel(document.parsing_status)}
      </Badge>
      {showSaveToBrief ? (
        <SaveToBriefControl profileId={document.parsed_profile_id!} profileName={displayName} />
      ) : null}
      {showRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive"
          onClick={() => setDeleteOpen(true)}
          disabled={isDeleting}
          aria-label={`Remove ${displayName} from talent`}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </div>
  )

  const body = (
    <div className="pointer-events-none space-y-2">
      {roleLine ? (
        <p className={cn('text-sm text-muted-foreground', isCompact && 'text-xs')}>{roleLine}</p>
      ) : null}
      {locationLine ? (
        <div
          className={cn(
            'flex items-center gap-2 text-sm text-muted-foreground',
            isCompact && 'text-xs',
          )}
        >
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <span>{locationLine}</span>
        </div>
      ) : null}
      {skills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft" className="text-xs font-normal">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
      {!isCompact && metadataLine ? (
        <p className="text-xs text-muted-foreground">{metadataLine}</p>
      ) : null}
      {document.parsing_error ? (
        <p className="text-xs text-destructive">{document.parsing_error}</p>
      ) : null}
    </div>
  )

  const footerMeta = isHub ? (
    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <Calendar className="size-3.5" aria-hidden />
        Uploaded {formatBriefDate(document.uploaded_at)}
      </span>
    </div>
  ) : null

  return (
    <>
      <PersonListCard
        variant={cardVariant}
        className={cn(
          isCompact && 'shadow-none',
          className,
        )}
        person={personModel}
        titleBadge={openToWorkTitleBadge(preview?.open_to_work)}
        headerAside={headerAside}
        body={body}
        footer={footerMeta}
        onOpen={canPreview ? onOpen : undefined}
        openAriaLabel={canPreview ? `View profile for ${displayName}` : undefined}
      />

      {showRemove ? (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove from talent?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the uploaded file and its parsed profile from your talent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  )
}
